import { Injectable, OnDestroy, TemplateRef } from '@angular/core';
import { NavigationEnd, NavigationStart } from '@angular/router';
import { PageUrls } from '@constants/page-urls.constants';
import {
  DEFAULT_LAYOUT_STATE,
  DEFAULT_LOGO_URL,
  DEFAULT_PAGE_TITLE_STATE,
  DEFAULT_TENANT,
  LayoutState,
  PageTitleState
} from '@models/layout-state.model';
import { SessionService } from '@services/session/session.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ViewService } from 'tangoe-design-system';

import { LayoutAnimationService } from '../layout-animation-service/layout-animation.service';
import { NavigationService } from '../navigation-service/navigation.service';

@Injectable({
  providedIn: 'root'
})
export class LayoutMediatorService implements OnDestroy {
  private layout_state_subject: BehaviorSubject<LayoutState> = new BehaviorSubject({
    ...DEFAULT_LAYOUT_STATE
  });
  private page_title_subject: BehaviorSubject<PageTitleState> = new BehaviorSubject({
    ...DEFAULT_PAGE_TITLE_STATE
  });
  private page_title_content_subject: BehaviorSubject<TemplateRef<any> | null> =
    new BehaviorSubject(null as TemplateRef<any> | null);
  private error_subject: BehaviorSubject<string | undefined> = new BehaviorSubject(
    undefined as string | undefined
  );
  private subscriptions: Subscription = new Subscription();

  constructor(
    private navigation_svc: NavigationService,
    private animation_svc: LayoutAnimationService,
    private view_svc: ViewService,
    private session_svc: SessionService
  ) {
    this.subscriptions.add(
      this.navigation_svc.navigationStart.subscribe((event: NavigationStart) => {
        // Overrride default layout state when user is loggedIn
        this.layout_state_subject.next({
          ...DEFAULT_LAYOUT_STATE,
          tenant_name: this.session_svc.getTenantInfo()?.name || DEFAULT_TENANT,
          logo_url: this.session_svc.getTenantInfo().branding?.logo_file || DEFAULT_LOGO_URL
        });

        this.onNavigationStart();
      })
    );

    this.subscriptions.add(
      this.navigation_svc.navigationEnd.subscribe((event: NavigationEnd) =>
        this.animation_svc.onCurrentAnimationDone(() => {
          this.setLayoutState(
            this.buildLayoutState(event.urlAfterRedirects, this.error_subject.value)
          );
        })
      )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  layoutState(): BehaviorSubject<LayoutState> {
    return this.layout_state_subject;
  }

  pageTitleState(): BehaviorSubject<PageTitleState> {
    return this.page_title_subject;
  }

  pageTitleContent(): BehaviorSubject<TemplateRef<any> | null> {
    return this.page_title_content_subject;
  }

  error(): BehaviorSubject<string | undefined> {
    return this.error_subject;
  }

  setPageTitleState(state: Partial<PageTitleState>): void {
    const update: boolean = Object.entries(state).some(
      ([key, value]: [string, any]) =>
        value !== this.page_title_subject.value[key as keyof PageTitleState]
    );
    if (update) this.page_title_subject.next({ ...this.page_title_subject.value, ...state });
  }

  setPageTitleContent(content: TemplateRef<any> | null): void {
    this.page_title_content_subject.next(content);
  }

  setError(error: string | undefined): void {
    if (error === this.error_subject.value) return;

    if (
      error === 'resource' ||
      !this.view_svc.errorContainer() ||
      !this.view_svc.contentContainer()
    ) {
      this.dispatchError(this.navigation_svc.url, error);
      return;
    }

    this.animation_svc
      .LayoutFadeOutAndIn()
      .subscribe((event: 'start' | 'fadeOutDone' | 'fadeInStart' | 'done') => {
        if (event === 'fadeOutDone') this.dispatchError(this.navigation_svc.url, error);
      });
  }

  private setLayoutState(state: LayoutState): void {
    const update: boolean = Object.entries(state).some(
      ([key, value]: [string, any]) =>
        value !== this.layout_state_subject.value[key as keyof LayoutState]
    );
    if (update) this.layout_state_subject.next(state);
  }

  private dispatchError(url: string, error?: string): void {
    if (error === this.error_subject.value) return;

    this.setLayoutState(this.buildLayoutState(url, error));
    this.switchContainerElements(error);
    this.error_subject.next(error);
  }

  private switchContainerElements(error: string | undefined): void {
    const fade_out_elt: HTMLElement | null =
      this.view_svc[this.error_subject.value ? 'errorContainer' : 'contentContainer']();
    const fade_in_elt: HTMLElement | null =
      this.view_svc[error ? 'errorContainer' : 'contentContainer']();

    if (fade_out_elt) this.view_svc.addClassWithAnimationFrame(fade_out_elt, 'd-none');
    if (fade_in_elt) this.view_svc.removeClassWithAnimationFrame(fade_in_elt, 'd-none');
  }

  private buildLayoutState(url: string, error: string | undefined): LayoutState {
    const logo_url =
      this.session_svc.getTenantInfo()?.branding?.logo_file || DEFAULT_LAYOUT_STATE.logo_url;
    const tenant_name =
      this.session_svc.getTenantInfo()?.branding?.privacy_notice_company_name ||
      DEFAULT_LAYOUT_STATE.tenant_name;
    const apply_branding =
      url === PageUrls.LOGOUT || (url !== PageUrls.LOGIN && this.session_svc.hasUser());

    return {
      display_header: !error || this.session_svc.hasUser(),
      display_page_title: this.displayPageTitle(url) && this.session_svc.hasUser(),
      can_navigate_to_dashboard: this.canNavigateToDashboard(url) && this.session_svc.hasUser(),
      logo_url: apply_branding ? logo_url : DEFAULT_LAYOUT_STATE.logo_url,
      tenant_name: apply_branding ? tenant_name : DEFAULT_LAYOUT_STATE.tenant_name
    };
  }

  private canNavigateToDashboard(url: string): boolean {
    return url !== PageUrls.HOME && !url.startsWith('/auth/');
  }

  private displayPageTitle(url: string): boolean {
    return this.canNavigateToDashboard(url) && !url.startsWith('/shop/order-confirmation/');
  }

  private onNavigationStart(): void {
    if (!this.error_subject.value) return;

    const sub: Subscription = this.animation_svc
      .layoutFadeOut()
      .subscribe((event: 'start' | 'done') => {
        if (event === 'done') {
          this.dispatchError(this.navigation_svc.url);
          sub.unsubscribe();
        }
      });
  }
}
